import React, { useEffect, useRef, useState } from 'react';
import InputBox from '../../common/InputBox';
import { validateForm } from '../../function/FormValidation';
import { phoneNumberRangeRegex } from '../../function/Regex';
import Button from '../../common/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createUser, updateUser } from '../../api/SettingUser';
import ToastNotification from '../../common/ToastNotification';
import { FaArrowLeft } from 'react-icons/fa6';
import { countrycodes } from '../../country-code/CountryCodes';
import CountryCode from '../../common/CountryCode';
import { getSubscriptionDetail } from '../../common/CommonFunction';
import countries from '../../country-code/Countries';

interface Country {
  code: string;
  flag: string;
  name: string;
  teleCode: string;
}

function CreateUserSetting() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userData = location.state?.userData;
  const companyUuid = sessionStorage.getItem('companyUuid');
  const shopId = sessionStorage.getItem('shopId');
  const businessType = sessionStorage.getItem('businessType');

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    contactNumber: '',
    employeeId: '',
    position: '',
    address: '',
    Countrycode: '',
    Countryname: '',
    addressState: '',
    addressCountry: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [disable, setDisable] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ''
    }));
  };

  const handleUpdateUserProfile = async (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
      | undefined
  ) => {
    e?.preventDefault();
    const { isValid, errors } = validateForm(formData, [
      'name',
      'email',
      'contactNumber',
      'employeeId',
      'position',
      'address',
      'addressState',
      'addressCountry'
    ]);

    const contactValid = validatePhoneByCountryCode(
      formData.contactNumber,
      formData.Countrycode || '+91'
    );

    // Custom error messages based on country rules
    if (!contactValid) {
      const digits = countries.find((r) => r.code === (formData.Countrycode || '+91'))?.digits;
      errors.contactNumber = `Contact number must be ${digits} digits.`;
    }

    setErrors(errors);
    if (errors && Object.keys(errors).length > 0) {
      setDisable(false);
      return;
    } else {
      setDisable(true);
    }
    if (isValid) {
      if (location.pathname.startsWith('/settings/users/update-user')) {
        try {
          const response = await updateUser(
            {
              uuid: id || '',
              name: formData.name || '',
              email: formData.email || '',
              // companyName: formData.companyName || '',
              contactNumber: formData.contactNumber || '',
              employeeId: formData.employeeId || '',
              position: formData.position || '',
              address: formData.address || '',
              code: formData.Countrycode || '',
              country: formData.Countryname || '',
              addressState: formData.addressState || '',
              addressCountry: formData.addressCountry || ''
            },
            companyUuid ? companyUuid : ''
          );
          if (response.ok) {
            const data = await response.json();

            ToastNotification({
              message: data.message
            });
            getSubscriptionDetail(businessType, shopId);
            setTimeout(() => {
              navigate('/settings/users');
              setDisable(false);
            }, 1500);
          } else {
            const errorData = await response.json();
            ToastNotification({
              message: errorData.message,
              type: 'warning'
            });
            setDisable(false);
          }
        } catch (error) {
          setDisable(true);
        }
      } else {
        try {
          const response = await createUser(
            {
              name: formData.name || '',
              email: formData.email || '',
              // companyName: formData.companyName || '',
              contactNumber: formData.contactNumber || '',
              employeeId: formData.employeeId || '',
              position: formData.position || '',
              address: formData.address || '',
              code: formData.Countrycode || '+91',
              country: formData.Countryname || 'India',
              addressState: formData.addressState || '',
              addressCountry: formData.addressCountry || ''
            },
            companyUuid ? companyUuid : ''
          );
          if (response.ok) {
            const data = await response.json();
            ToastNotification({
              message: data.message
            });
            getSubscriptionDetail(businessType, shopId);
            setTimeout(() => {
              navigate('/settings/users');
              setFormData({
                name: '',
                email: '',
                companyName: '',
                contactNumber: '',
                employeeId: '',
                position: '',
                address: '',
                Countrycode: '+91',
                Countryname: 'India',
                addressState: '',
                addressCountry: ''
              });
              setDisable(false);
            }, 1500);
          } else {
            const errorData = await response.json();
            ToastNotification({
              message: errorData.message,
              type: 'warning'
            });
            setDisable(false);
          }
        } catch (error) {
          setDisable(false);
        }
      }
    }
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (country: Country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setErrors((prevErrors) => ({
      ...prevErrors,
      contactNumber: ''
    }));
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredCountries = countrycodes?.filter(
    (country: Country) =>
      country.teleCode.includes(searchQuery) ||
      country.code.toLowerCase().includes(searchQuery) ||
      country.name.toLowerCase().includes(searchQuery)
  );

  const handleGoBack = () => {
    navigate('/settings/users');
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        if (userData) {
          setFormData({
            name: userData?.fullName || '',
            email: userData?.email || '',
            companyName: userData?.account?.companyName || '',
            contactNumber: userData?.phone || '',
            employeeId: userData?.employeeId || '',
            position: userData?.position || '',
            address: userData?.address || '',
            Countrycode: userData?.code || countrycodes[0].teleCode,
            Countryname: userData?.country || '',
            addressState: userData?.addressState || '',
            addressCountry: userData?.addressCountry || ''
          });
          const selected_Country =
            countrycodes.find((country) => country.teleCode === userData?.code) || countrycodes[0];
          if (selected_Country) {
            setSelectedCountry(selected_Country);
          }
        } else {
          setSelectedCountry(countrycodes[0]);
        }
      } catch (error) {
        console.error('Error fetching company details:', error);
        setErrors((prev) => ({ ...prev, fetch: 'Failed to fetch company details.' }));
      }
    };

    fetchCompanyDetails();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      setFormData((prevData: any) => ({
        ...prevData,
        Countryname: selectedCountry.name,
        Countrycode: selectedCountry.teleCode
      }));
    }
  }, [selectedCountry]);

  const validatePhoneByCountryCode = (phone: string, countryCode: string): boolean => {
    const rule = countries.find((rule) => rule.code === countryCode);
    if (!rule) return false;

    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === rule.digits;
  };

  return (
    <div className="flex flex-col flex-1 bg-[#1E1E1E]">
      <div className="flex px-5">
        <div className="">
          <FaArrowLeft
            onClick={handleGoBack}
            className="text-[#006BEA] w-5 h-5 mt-3 cursor-pointer"
          />
        </div>
        <h1 className="h-[26px] px-2  mt-4 md:mt-3 mb-[27px] pl-4 sm:text-[12px] sm:leading-[29.18px] md:text-base md:leading-[25.78px] font-medium">
          Users Profile
        </h1>
      </div>
      <form
        className="flex flex-col justify-between"
        onSubmit={handleUpdateUserProfile}
        method="Post"
      >
        <div className="space-y-0.5 pr-4 px-5">
          {' '}
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">Name</label>
            <div className="w-full">
              <InputBox
                name="name"
                type="text"
                value={formData.name}
                placeholder="Enter Your Name"
                handleChange={handleChange}
                error={errors.name}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">Email</label>
            <div className="w-full">
              <InputBox
                name="email"
                type="email"
                value={formData.email}
                placeholder="Enter Your Email"
                handleChange={handleChange}
                error={errors.email}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">
              Contact Number
            </label>
            <div className="w-full flex gap-2">
              <CountryCode
                formData={formData}
                isOpen={isOpen}
                searchQuery={searchQuery}
                selectedCountry={selectedCountry}
                dropdownRef={dropdownRef}
                filteredCountries={filteredCountries}
                toggleDropdown={toggleDropdown}
                handleSearch={handleSearch}
                handleSelect={handleSelect}
                countrycodes={countrycodes}
              />
              <div className="flex-grow">
                <InputBox
                  name="contactNumber"
                  type="text"
                  value={formData.contactNumber}
                  placeholder="Enter your Phone Number"
                  error={errors.contactNumber}
                  handleChange={(e) => {
                    const value = e.target.value;
                    if (phoneNumberRangeRegex.test(value)) {
                      handleChange(e);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">Employee ID</label>
            <div className="w-full">
              <InputBox
                name="employeeId"
                type="text"
                value={formData.employeeId}
                placeholder="Enter Your Employee Id"
                handleChange={handleChange}
                error={errors.employeeId}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">Position</label>
            <div className="w-full">
              <InputBox
                name="position"
                type="text"
                value={formData.position}
                placeholder="Enter Your Position"
                handleChange={handleChange}
                error={errors.position}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">Address</label>
            <div className="w-full">
              <InputBox
                name="address"
                type="text"
                value={formData.address}
                placeholder="Enter Your Address"
                handleChange={handleChange}
                error={errors.address}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">State</label>
            <div className="w-full">
              <InputBox
                name="addressState"
                type="text"
                value={formData.addressState}
                placeholder="Enter Your State"
                handleChange={handleChange}
                error={errors.addressState}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 ps-4">
            <label className="text-xs md:text-sm font-medium text-light w-[40%]">Country</label>
            <div className="w-full">
              <InputBox
                name="addressCountry"
                type="text"
                value={formData.addressCountry}
                placeholder="Enter Your Country"
                handleChange={handleChange}
                error={errors.addressCountry}
              />
            </div>
          </div>
        </div>
      </form>
      <div className="flex justify-end items-end px-5">
        <Button
          isFormValid={true}
          btnText={`${location.pathname.startsWith('/settings/users/update-user') ? 'Update' : 'Create'}`}
          disable={disable}
          textColor="#FFFFFF"
          backGroundColor="#006BEA"
          onClick={(event) => handleUpdateUserProfile(event)}
        />
      </div>
      <div className="mb-4"></div>
      {disable && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>

          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="loader border-t-4 border-[#006BEA] rounded-full w-12 h-12 animate-spin"></div>
          </div>
        </>
      )}
    </div>
  );
}

export default CreateUserSetting;
